import {Injectable} from '@angular/core';
import {Document, DocumentType, Equipment, Partner} from '@app/shared';
import {environment} from '../../../environments/environment';

export type DocumentRequirementKey =
  | 'gd'
  | 'eir'
  | 'license'
  | 'identityCard'
  | 'technicalReview'
  | 'emissionsCertificate'
  | 'circulationPermit'
  | 'soap'
  | 'propertyCard'
  | 'deconsolidationReport';

interface DocumentRestrictionType {
  proIds?: number[];
  aliases: string[];
}

type DocumentRestrictionTypes = Record<DocumentRequirementKey, DocumentRestrictionType>;

export interface DocumentLike {
  id?: number | null;
  name?: string | null;
  type?: string | null;
  documentTypeId?: number | null;
  documentType?: DocumentType | null;
  expirationDate?: string | Date | null;
  validationStatus?: 'NonValidated' | 'ValidationPending' | 'Validated' | string | null;
  file?: File | string | null;
  isExisting?: boolean;
  isDeleted?: boolean;
}

export interface DocumentRestrictionIssue {
  key: DocumentRequirementKey;
  label: string;
  reason: 'missing' | 'expired' | 'notValidated';
}

export type DocumentRequirementStatus = 'valid' | 'expired' | 'missing' | 'notValidated';

export interface DocumentRequirementStatusItem {
  key: DocumentRequirementKey;
  label: string;
  status: DocumentRequirementStatus;
}

export interface DocumentRestrictionResult {
  valid: boolean;
  issues: DocumentRestrictionIssue[];
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentRestrictionsService {
  private readonly documentTypes = environment.documentRestrictionTypes as DocumentRestrictionTypes;

  private readonly labels: Record<DocumentRequirementKey, string> = {
    gd: 'GD',
    eir: 'EIR',
    license: 'Licencia',
    identityCard: 'Cédula de identidad',
    technicalReview: 'Revisión técnica vehicular',
    emissionsCertificate: 'Certificado de emisiones',
    circulationPermit: 'Tarjeta de circulación/Permiso de circulación',
    soap: 'SOAP/SOAT',
    propertyCard: 'Tarjeta de propiedad/Padrón',
    deconsolidationReport: 'Informe de desconsolidación',
  };

  private readonly driverRequirements: DocumentRequirementKey[] = [
    'license',
    'identityCard',
  ];

  private readonly truckRequirements: DocumentRequirementKey[] = [
    'technicalReview',
    'emissionsCertificate',
    'circulationPermit',
    'soap',
    'propertyCard',
  ];

  private readonly trailerRequirements: DocumentRequirementKey[] = [
    'technicalReview',
    'circulationPermit',
    'propertyCard',
  ];

  private readonly limitedUploadRequirements: DocumentRequirementKey[] = [
    'gd',
    'eir',
    'deconsolidationReport',
  ];

  private readonly aiUploadDocumentTypeIds: number[] = environment.aiUploadDocumentTypeIds;

  validateDriver(driver: Partner | undefined | null, documentTypes: DocumentType[] = []): DocumentRestrictionResult {
    return this.validateDocuments(driver?.documents || [], this.driverRequirements, documentTypes);
  }

  validateDriverDocuments(documents: DocumentLike[], documentTypes: DocumentType[] = []): DocumentRestrictionResult {
    return this.validateDocuments(documents, this.driverRequirements, documentTypes);
  }

  validateEquipment(equipment: Equipment | undefined | null, documentTypes: DocumentType[] = []): DocumentRestrictionResult {
    return this.validateEquipmentDocuments(equipment?.documents || [], equipment?.equipmentTypeId, documentTypes);
  }

  validateEquipmentDocuments(
    documents: DocumentLike[],
    equipmentTypeId: number | undefined | null,
    documentTypes: DocumentType[] = []
  ): DocumentRestrictionResult {
    return this.validateDocuments(documents, this.getEquipmentRequirementKeys(equipmentTypeId), documentTypes);
  }

  getEquipmentRequirementKeys(equipmentTypeId: number | undefined | null): DocumentRequirementKey[] {
    if (equipmentTypeId === 1) {
      return this.truckRequirements;
    }

    if (equipmentTypeId === 2) {
      return this.trailerRequirements;
    }

    return [];
  }

  /**
   * Devuelve los documentTypeId que aparecen más de una vez entre los documentos
   * vigentes de una misma entidad. Los documentos marcados como eliminados no
   * cuentan, de modo que reemplazar un documento (eliminar y volver a subir) sigue
   * siendo posible.
   */
  getDuplicatedDocumentTypeIds(documents: DocumentLike[]): number[] {
    const seen = new Set<number>();
    const duplicated = new Set<number>();

    for (const document of documents) {
      if (document.isDeleted || !document.documentTypeId) {
        continue;
      }

      if (seen.has(document.documentTypeId)) {
        duplicated.add(document.documentTypeId);
        continue;
      }

      seen.add(document.documentTypeId);
    }

    return [...duplicated];
  }

  hasDuplicatedDocumentTypes(documents: DocumentLike[]): boolean {
    return this.getDuplicatedDocumentTypeIds(documents).length > 0;
  }

  /**
   * Tipos duplicados en los que interviene al menos un documento añadido en esta
   * sesión. Solo estos bloquean el guardado: un duplicado que ya venía de antes se
   * avisa, pero bloquearlo dejaría sin poder editar las entidades que arrastran
   * datos duplicados de antes de esta validación.
   */
  hasBlockingDuplicatedDocumentTypes(documents: DocumentLike[]): boolean {
    return this.getDuplicatedDocumentTypeIds(documents).some(typeId =>
      documents.some(
        document =>
          !document.isDeleted &&
          !document.isExisting &&
          document.documentTypeId === typeId
      )
    );
  }

  /**
   * Indica si un documento concreto está repitiendo un tipo ya usado por otro
   * documento vigente de la misma entidad.
   */
  isDuplicatedDocumentType(documents: DocumentLike[], document: DocumentLike): boolean {
    if (!document.documentTypeId) {
      return false;
    }

    return documents.some(
      other =>
        other !== document &&
        !other.isDeleted &&
        other.documentTypeId === document.documentTypeId
    );
  }

  buildDuplicatedDocumentTypesMessage(documents: DocumentLike[], documentTypes: DocumentType[] = []): string {
    const duplicated = this.getDuplicatedDocumentTypeIds(documents);

    if (duplicated.length === 0) {
      return '';
    }

    const names = duplicated.map(
      typeId => documentTypes.find(documentType => documentType.id === typeId)?.name || 'Sin tipo'
    );

    return names.length === 1
      ? `Hay más de un documento de tipo ${names[0]}. Elimina el que sobre para dejar solo uno.`
      : `Hay tipos de documento repetidos: ${names.join(', ')}. Elimina los que sobren para dejar solo uno de cada tipo.`;
  }

  getRequirementLabel(key: DocumentRequirementKey): string {
    return this.labels[key];
  }

  getRequirementStatusLabel(status: DocumentRequirementStatus): string {
    const labels: Record<DocumentRequirementStatus, string> = {
      valid: 'Válido',
      expired: 'Expirado',
      missing: 'Faltante',
      notValidated: 'No validado',
    };

    return labels[status];
  }

  getRequirementStatusClass(status: DocumentRequirementStatus): string {
    return `requirement-status requirement-status-${status}`;
  }

  getLimitedUploadDocumentTypes(documentTypes: DocumentType[]): DocumentType[] {
    return documentTypes.filter(documentType =>
      this.limitedUploadRequirements.some(key => this.matchesDocumentType(documentType, key))
    );
  }

  /**
   * Tipos de documento admitidos en «Subir y analizar con IA». El motor de IA solo
   * lee el BL, así que cualquier otro tipo acaba en «Error en análisis». Se eligen por
   * id (configurado por entorno), nunca por nombre.
   */
  getAiUploadDocumentTypes(documentTypes: DocumentType[]): DocumentType[] {
    return documentTypes.filter(documentType => this.aiUploadDocumentTypeIds.includes(documentType.id));
  }

  getRequiredDocumentTypesForEquipment(equipmentTypeId: number | undefined | null, documentTypes: DocumentType[]): DocumentType[] {
    return this.getDocumentTypesForRequirements(this.getEquipmentRequirementKeys(equipmentTypeId), documentTypes);
  }

  getRequiredDocumentTypesForDriver(documentTypes: DocumentType[]): DocumentType[] {
    return this.getDocumentTypesForRequirements(this.driverRequirements, documentTypes);
  }

  getEquipmentRequirementStatuses(
    documents: DocumentLike[],
    equipmentTypeId: number | undefined | null,
    documentTypes: DocumentType[] = []
  ): DocumentRequirementStatusItem[] {
    return this.getRequirementStatuses(documents, this.getEquipmentRequirementKeys(equipmentTypeId), documentTypes);
  }

  getDriverRequirementStatuses(
    documents: DocumentLike[],
    documentTypes: DocumentType[] = []
  ): DocumentRequirementStatusItem[] {
    return this.getRequirementStatuses(documents, this.driverRequirements, documentTypes);
  }

  validateDocuments(
    documents: DocumentLike[],
    requirements: DocumentRequirementKey[],
    documentTypes: DocumentType[] = []
  ): DocumentRestrictionResult {
    const activeDocuments = documents.filter(document => !document.isDeleted);
    const issues: DocumentRestrictionIssue[] = [];

    for (const requirement of requirements) {
      const matchingDocuments = activeDocuments.filter(document => this.matchesDocument(document, requirement, documentTypes));

      if (matchingDocuments.length === 0) {
        issues.push({
          key: requirement,
          label: this.labels[requirement],
          reason: 'missing',
        });
        continue;
      }

      const status = this.getRequirementStatus(matchingDocuments);

      if (status === 'valid') {
        continue;
      }

      if (status === 'notValidated') {
        issues.push({
          key: requirement,
          label: this.labels[requirement],
          reason: 'notValidated',
        });
        continue;
      }

      if (status === 'expired') {
        issues.push({
          key: requirement,
          label: this.labels[requirement],
          reason: 'expired',
        });
      }
    }

    return {
      valid: issues.length === 0,
      issues,
      message: this.buildMessage(issues),
    };
  }

  hasValidExpirationDate(expirationDate: string | Date | null | undefined): boolean {
    if (!expirationDate) {
      return false;
    }

    const date = expirationDate instanceof Date ? expirationDate : new Date(expirationDate);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    return this.normalizeDate(date).getTime() >= this.normalizeDate(new Date()).getTime();
  }

  private getRequirementStatuses(
    documents: DocumentLike[],
    requirements: DocumentRequirementKey[],
    documentTypes: DocumentType[]
  ): DocumentRequirementStatusItem[] {
    const activeDocuments = documents.filter(document => !document.isDeleted);

    return requirements.map(requirement => {
      const matchingDocuments = activeDocuments.filter(document => this.matchesDocument(document, requirement, documentTypes));
      const status = matchingDocuments.length > 0
        ? this.getRequirementStatus(matchingDocuments)
        : 'missing';

      return {
        key: requirement,
        label: this.labels[requirement],
        status,
      };
    });
  }

  private getDocumentTypesForRequirements(requirements: DocumentRequirementKey[], documentTypes: DocumentType[]): DocumentType[] {
    return requirements
      .map(requirement => documentTypes.find(documentType => this.matchesDocumentType(documentType, requirement)))
      .filter((documentType): documentType is DocumentType => !!documentType);
  }

  private matchesDocument(document: DocumentLike, requirement: DocumentRequirementKey, documentTypes: DocumentType[]): boolean {
    const rule = this.documentTypes[requirement];

    if (environment.production && document.documentTypeId && rule.proIds?.includes(document.documentTypeId)) {
      return true;
    }

    const names = [
      document.documentType?.name,
      document.type,
      document.name,
      documentTypes.find(documentType => documentType.id === document.documentTypeId)?.name,
    ]
      .filter((value): value is string => !!value)
      .map(value => this.normalizeText(value));

    const aliases = rule.aliases.map(alias => this.normalizeText(alias));
    return names.some(name => aliases.includes(name));
  }

  private isValidated(document: DocumentLike): boolean {
    return document.validationStatus === 'Validated';
  }

  private getRequirementStatus(matchingDocuments: DocumentLike[]): Exclude<DocumentRequirementStatus, 'missing'> {
    const hasValidatedAndCurrent = matchingDocuments.some(document =>
      this.isValidated(document) && this.hasValidExpirationDate(document.expirationDate)
    );

    if (hasValidatedAndCurrent) {
      return 'valid';
    }

    const hasCurrentNotValidated = matchingDocuments.some(document =>
      !this.isValidated(document) && this.hasValidExpirationDate(document.expirationDate)
    );

    if (hasCurrentNotValidated) {
      return 'notValidated';
    }

    const hasValidated = matchingDocuments.some(document => this.isValidated(document));
    return hasValidated ? 'expired' : 'notValidated';
  }

  private matchesDocumentType(documentType: DocumentType, requirement: DocumentRequirementKey): boolean {
    const rule = this.documentTypes[requirement];

    if (environment.production && rule.proIds?.includes(documentType.id)) {
      return true;
    }

    const normalizedName = this.normalizeText(documentType.name);
    return rule.aliases.some(alias => this.normalizeText(alias) === normalizedName);
  }

  private buildMessage(issues: DocumentRestrictionIssue[]): string {
    if (issues.length === 0) {
      return '';
    }

    const missing = issues.filter(issue => issue.reason === 'missing').map(issue => issue.label);
    const expired = issues.filter(issue => issue.reason === 'expired').map(issue => issue.label);
    const notValidated = issues.filter(issue => issue.reason === 'notValidated').map(issue => issue.label);
    const parts: string[] = [];

    if (missing.length > 0) {
      parts.push(`Faltan documentos requeridos: ${missing.join(', ')}.`);
    }

    if (notValidated.length > 0) {
      parts.push(`Hay documentos no validados: ${notValidated.join(', ')}.`);
    }

    if (expired.length > 0) {
      parts.push(`Hay documentos vencidos: ${expired.join(', ')}.`);
    }

    return parts.join(' ');
  }

  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }
}
